import React, { useEffect, useState, useRef } from 'react';
import { motion } from 'framer-motion';
import { Trash2, MousePointer2, Circle, Square, RectangleHorizontal } from 'lucide-react';
type TableType = 'round' | 'rect' | 'head';
interface Table {
  id: string;
  type: TableType;
  x: number;
  y: number;
  seats: number;
}
interface Venue {
  id: string;
  name: string;
  capacity: number;
  width: number;
  height: number;
}
const VENUES: Venue[] = [{
  id: 'azure',
  name: 'The Azure Hall',
  capacity: 300,
  width: 800,
  height: 600
}, {
  id: 'sapphire',
  name: 'The Sapphire Room',
  capacity: 200,
  width: 700,
  height: 500
}, {
  id: 'crystal',
  name: 'The Crystal Lounge',
  capacity: 150,
  width: 600,
  height: 400
}];
export function FloorPlanPreview() {
  const [selectedVenue, setSelectedVenue] = useState<Venue>(VENUES[0]);
  const [tables, setTables] = useState<Table[]>([]);
  const [selectedTool, setSelectedTool] = useState<TableType | 'pointer'>('pointer');
  const [draggingId, setDraggingId] = useState<string | null>(null);
  const svgRef = useRef<SVGSVGElement>(null);
  const getTableProps = (type: TableType) => {
    switch (type) {
      case 'round':
        return {
          width: 40,
          height: 40,
          seats: 8,
          rx: 20
        };
      case 'rect':
        return {
          width: 60,
          height: 40,
          seats: 10,
          rx: 4
        };
      case 'head':
        return {
          width: 120,
          height: 30,
          seats: 12,
          rx: 4
        };
    }
  };
  const getMouseCoords = (e: React.MouseEvent | MouseEvent) => {
    if (!svgRef.current) return {
      x: 0,
      y: 0
    };
    const CTM = svgRef.current.getScreenCTM();
    if (!CTM) return {
      x: 0,
      y: 0
    };
    return {
      x: (e.clientX - CTM.e) / CTM.a,
      y: (e.clientY - CTM.f) / CTM.d
    };
  };
  const handleSvgClick = (e: React.MouseEvent) => {
    if (selectedTool === 'pointer' || draggingId) return;
    const coords = getMouseCoords(e);
    const props = getTableProps(selectedTool);
    const newTable: Table = {
      id: Math.random().toString(36).substr(2, 9),
      type: selectedTool,
      x: coords.x - props.width / 2,
      y: coords.y - props.height / 2,
      seats: props.seats
    };
    setTables([...tables, newTable]);
    setSelectedTool('pointer'); // Auto-switch back to pointer
  };
  const handleMouseDown = (e: React.MouseEvent, id: string) => {
    if (selectedTool !== 'pointer') return;
    e.stopPropagation();
    setDraggingId(id);
  };
  const handleMouseMove = (e: React.MouseEvent) => {
    if (!draggingId) return;
    const coords = getMouseCoords(e);
    setTables(tables.map(t => {
      if (t.id === draggingId) {
        const props = getTableProps(t.type);
        // Constrain to bounds
        const x = Math.max(0, Math.min(coords.x - props.width / 2, selectedVenue.width - props.width));
        const y = Math.max(0, Math.min(coords.y - props.height / 2, selectedVenue.height - props.height));
        return {
          ...t,
          x,
          y
        };
      }
      return t;
    }));
  };
  const handleMouseUp = () => {
    setDraggingId(null);
  };
  const totalSeats = tables.reduce((sum, t) => sum + t.seats, 0);
  return <section className="py-24 px-6 md:px-12 bg-h2o-950 relative" data-id="element-27">
      <div className="max-w-7xl mx-auto" data-id="element-28">
        <div className="text-center mb-12" data-id="element-29">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4" data-id="element-30">
            Plan Your Perfect Layout
          </h2>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto" data-id="element-31">
            Drag and drop tables to design your ideal seating arrangement in
            real time.
          </p>
        </div>

        <div className="flex flex-col lg:flex-row gap-8" data-id="element-32">
          {/* Toolbar & Stats */}
          <div className="w-full lg:w-64 flex flex-col gap-6" data-id="element-33">
            <div className="bg-h2o-900 border border-slate-700/50 rounded-lg p-6" data-id="element-34">
              <label className="block text-sm font-medium text-slate-300 mb-2" data-id="element-35">
                Select Venue
              </label>
              <select value={selectedVenue.id} onChange={e => {
              setSelectedVenue(VENUES.find(v => v.id === e.target.value) || VENUES[0]);
              setTables([]); // Reset tables on venue change
            }} className="w-full bg-h2o-800 border border-slate-600 rounded-md px-3 py-2 text-white focus:border-h2o-cyan outline-none" data-id="element-36">
                {VENUES.map(v => <option key={v.id} value={v.id} data-id="element-37">
                    {v.name} (Cap: {v.capacity})
                  </option>)}
              </select>
            </div>

            <div className="bg-h2o-900 border border-slate-700/50 rounded-lg p-6" data-id="element-38">
              <h3 className="text-sm font-medium text-slate-300 mb-4 uppercase tracking-wider" data-id="element-39">
                Tools
              </h3>
              <div className="grid grid-cols-2 gap-3" data-id="element-40">
                <button onClick={() => setSelectedTool('pointer')} className={`flex flex-col items-center justify-center p-3 rounded-md border transition-colors ${selectedTool === 'pointer' ? 'bg-h2o-cyan/20 border-h2o-cyan text-h2o-cyan' : 'bg-h2o-800 border-slate-700 text-slate-400 hover:border-slate-500'}`} data-id="element-41">
                  <MousePointer2 className="w-5 h-5 mb-1" data-id="element-42" />
                  <span className="text-xs" data-id="element-43">Select</span>
                </button>
                <button onClick={() => setSelectedTool('round')} className={`flex flex-col items-center justify-center p-3 rounded-md border transition-colors ${selectedTool === 'round' ? 'bg-h2o-cyan/20 border-h2o-cyan text-h2o-cyan' : 'bg-h2o-800 border-slate-700 text-slate-400 hover:border-slate-500'}`} data-id="element-44">
                  <Circle className="w-5 h-5 mb-1" data-id="element-45" />
                  <span className="text-xs" data-id="element-46">Round (8)</span>
                </button>
                <button onClick={() => setSelectedTool('rect')} className={`flex flex-col items-center justify-center p-3 rounded-md border transition-colors ${selectedTool === 'rect' ? 'bg-h2o-cyan/20 border-h2o-cyan text-h2o-cyan' : 'bg-h2o-800 border-slate-700 text-slate-400 hover:border-slate-500'}`} data-id="element-47">
                  <Square className="w-5 h-5 mb-1" data-id="element-48" />
                  <span className="text-xs" data-id="element-49">Rect (10)</span>
                </button>
                <button onClick={() => setSelectedTool('head')} className={`flex flex-col items-center justify-center p-3 rounded-md border transition-colors ${selectedTool === 'head' ? 'bg-h2o-cyan/20 border-h2o-cyan text-h2o-cyan' : 'bg-h2o-800 border-slate-700 text-slate-400 hover:border-slate-500'}`} data-id="element-50">
                  <RectangleHorizontal className="w-5 h-5 mb-1" data-id="element-51" />
                  <span className="text-xs" data-id="element-52">Head (12)</span>
                </button>
              </div>
            </div>

            <div className="bg-h2o-900 border border-slate-700/50 rounded-lg p-6" data-id="element-53">
              <h3 className="text-sm font-medium text-slate-300 mb-4 uppercase tracking-wider" data-id="element-54">
                Stats
              </h3>
              <div className="space-y-3" data-id="element-55">
                <div className="flex justify-between text-sm" data-id="element-56">
                  <span className="text-slate-400" data-id="element-57">Total Tables:</span>
                  <span className="text-white font-medium" data-id="element-58">
                    {tables.length}
                  </span>
                </div>
                <div className="flex justify-between text-sm" data-id="element-59">
                  <span className="text-slate-400" data-id="element-60">Total Seats:</span>
                  <span className={`font-medium ${totalSeats > selectedVenue.capacity ? 'text-red-400' : 'text-h2o-cyan'}`} data-id="element-61">
                    {totalSeats} / {selectedVenue.capacity}
                  </span>
                </div>
              </div>
              <button onClick={() => setTables([])} className="mt-6 w-full flex items-center justify-center gap-2 py-2 px-4 rounded-md border border-red-500/30 text-red-400 hover:bg-red-500/10 transition-colors text-sm" data-id="element-62">
                <Trash2 className="w-4 h-4" data-id="element-63" /> Clear All
              </button>
            </div>
          </div>

          {/* Floor Plan Canvas */}
          <div className="flex-1 bg-h2o-900 border border-slate-700/50 rounded-lg overflow-hidden relative min-h-[500px] flex items-center justify-center p-4" data-id="element-64">
            <div className="w-full h-full max-h-[600px] relative border-2 border-dashed border-slate-700 rounded-lg overflow-hidden bg-h2o-950/50" data-id="element-65">
              <svg ref={svgRef} viewBox={`0 0 ${selectedVenue.width} ${selectedVenue.height}`} className={`w-full h-full ${selectedTool !== 'pointer' ? 'cursor-crosshair' : 'cursor-default'}`} onClick={handleSvgClick} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp} onMouseLeave={handleMouseUp} data-id="element-66">
                {/* Room Features */}
                <rect x="0" y="0" width={selectedVenue.width} height={selectedVenue.height} fill="transparent" stroke="#1e3a5f" strokeWidth="4" rx="8" data-id="element-67" />

                {/* Stage Area */}
                <rect x={selectedVenue.width / 2 - 150} y="0" width="300" height="60" fill="#0b1b29" stroke="#183a54" strokeWidth="2" data-id="element-68" />
                <text x={selectedVenue.width / 2} y="35" fill="#475569" fontSize="20" textAnchor="middle" fontFamily="Inter" data-id="element-69">
                  STAGE / HEAD AREA
                </text>

                {/* Entrance */}
                <rect x={selectedVenue.width / 2 - 60} y={selectedVenue.height - 10} width="120" height="20" fill="#06b6d4" opacity="0.2" data-id="element-70" />
                <text x={selectedVenue.width / 2} y={selectedVenue.height - 20} fill="#475569" fontSize="16" textAnchor="middle" fontFamily="Inter" data-id="element-71">
                  ENTRANCE
                </text>

                {/* Bar Area */}
                <rect x={selectedVenue.width - 60} y={selectedVenue.height / 2 - 100} width="60" height="200" fill="#0b1b29" stroke="#183a54" strokeWidth="2" data-id="element-72" />
                <text x={selectedVenue.width - 30} y={selectedVenue.height / 2} fill="#475569" fontSize="16" textAnchor="middle" transform={`rotate(-90 ${selectedVenue.width - 30} ${selectedVenue.height / 2})`} fontFamily="Inter" data-id="element-73">
                  BAR AREA
                </text>

                {/* Tables */}
                {tables.map(table => {
                const props = getTableProps(table.type);
                const isDragging = draggingId === table.id;
                return <g key={table.id} transform={`translate(${table.x}, ${table.y})`} onMouseDown={e => handleMouseDown(e, table.id)} className={selectedTool === 'pointer' ? 'cursor-move' : ''} opacity={isDragging ? 0.7 : 1} data-id="element-74">
                      <rect width={props.width} height={props.height} rx={props.rx} fill="#1e3a5f" stroke={isDragging ? '#67e8f9' : '#06b6d4'} strokeWidth="2" data-id="element-75" />
                      <text x={props.width / 2} y={props.height / 2 + 5} fill="#f1f5f9" fontSize="14" textAnchor="middle" fontFamily="Inter" pointerEvents="none" data-id="element-76">
                        {props.seats}
                      </text>
                    </g>;
              })}
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>;
}